import { get, post } from "@/services/axiosUitils";

export type ManualExerciseType =
  | "running"
  | "walking"
  | "cycling"
  | "swimming"
  | "gym"
  | "other";

export type ManualExerciseIntensity = "low" | "medium" | "high";

export interface LogManualExerciseRequest {
  exercise_type: ManualExerciseType | string;
  exercise_name: string;
  duration_hours: number;
  duration_min: number;
  intensity: ManualExerciseIntensity;
  calories_kcal: number;
}

export interface ManualExerciseRecord {
  id?: string;
  exercise_type: string;
  exercise_name: string;
  duration_hours?: number;
  duration_min?: number;
  intensity: string;
  calories_kcal: number;
  logged_at?: string;
  date_local?: string;
}

export interface LogManualExerciseResponse {
  success?: { code: string; message: string };
  message?: string;
  exercise?: ManualExerciseRecord;
}

export interface ManualExerciseListResponse {
  success?: { code: string; message: string };
  exercises?: ManualExerciseRecord[];
  data?: ManualExerciseRecord[];
}

class ManualExerciseService {
  private static instance: ManualExerciseService;

  private constructor() {}

  public static getInstance(): ManualExerciseService {
    if (!ManualExerciseService.instance) {
      ManualExerciseService.instance = new ManualExerciseService();
    }
    return ManualExerciseService.instance;
  }

  async logManualExercise(
    payload: LogManualExerciseRequest
  ): Promise<LogManualExerciseResponse> {
    const response = await post<LogManualExerciseResponse>(
      "/food_scanner/manual-exercise/",
      payload,
      { showNotification: false }
    );

    if (response === undefined || response === null) {
      throw new Error("Failed to log exercise");
    }

    const errMsg =
      typeof (response as { error?: { message?: string } }).error?.message ===
      "string"
        ? (response as { error: { message: string } }).error.message
        : undefined;
    if (errMsg) {
      throw new Error(errMsg);
    }

    return response;
  }

  async getManualExercises(date?: string): Promise<ManualExerciseRecord[]> {
    const url = date
      ? `/food_scanner/manual-exercise/?date=${encodeURIComponent(date)}`
      : "/food_scanner/manual-exercise/";

    const response = await get<ManualExerciseListResponse & Record<string, unknown>>(
      url,
      { showNotification: false }
    );

    if (!response) return [];

    if (Array.isArray(response)) return response as ManualExerciseRecord[];

    const nested = response.data;
    if (Array.isArray(nested)) return nested as ManualExerciseRecord[];
    if (nested && typeof nested === "object" && Array.isArray((nested as { exercises?: unknown }).exercises)) {
      return (nested as { exercises: ManualExerciseRecord[] }).exercises;
    }

    return response.exercises ?? [];
  }
}

const manualExerciseService = ManualExerciseService.getInstance();
export default manualExerciseService;
